/**
 * Sentry Error Monitoring Configuration
 * 
 * To enable Sentry, set REACT_APP_SENTRY_DSN in your environment variables.
 * Get your DSN from https://sentry.io
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

export const initSentry = () => {
    if (!SENTRY_DSN) {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.info('Sentry DSN not configured. Error monitoring disabled.');
        }
        return;
    }

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,

        // Performance monitoring
        tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

        // SECURITY: Session replay disabled - captures screen recordings that would
        // include sensitive financial data (expense amounts, receipts, personal info).
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,

        // Filter out sensitive data
        beforeSend(event) {
            // Remove sensitive data from breadcrumbs
            if (event.breadcrumbs) {
                event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
                    if (breadcrumb.data) {
                        // Redact sensitive fields
                        const sensitiveKeys = ['password', 'token', 'authorization', 'cookie', 'secret'];
                        for (const key of Object.keys(breadcrumb.data)) {
                            if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
                                breadcrumb.data[key] = '[REDACTED]';
                            }
                        }
                    }
                    return breadcrumb;
                });
            }
            return event;
        },

        // Ignore certain errors
        ignoreErrors: [
            // Browser extension errors
            /^chrome-extension:\/\//,
            /^moz-extension:\/\//,
            // Network errors that are expected
            'Network Error',
            'Failed to fetch',
            // User-caused errors
            'ResizeObserver loop limit exceeded',
        ],
    });
};

// Utility to capture errors manually
export const captureError = (error, context = {}) => {
    if (SENTRY_DSN) {
        Sentry.captureException(error, { extra: context });
    }
};

// Utility to set user context
export const setUserContext = (user) => {
    if (SENTRY_DSN && user) {
        Sentry.setUser({
            id: user.id,
            email: user.email,
            // Don't include sensitive info like full name if not needed
        });
    }
};

// Utility to clear user context on logout
export const clearUserContext = () => {
    if (SENTRY_DSN) {
        Sentry.setUser(null);
    }
};

export default Sentry;
